'use client';

import { useState, type FormEvent } from 'react';
import Link from 'next/link';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { FormMessage } from '@/components/ui/FormMessage';
import { Card } from '@/components/ui/Card';

export default function RegisterPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('submitting');
    setError('');

    const form = new FormData(event.currentTarget);
    const name = String(form.get('name') ?? '');
    const email = String(form.get('email') ?? '');
    const password = String(form.get('password') ?? '');

    const { error: signUpError } = await authClient.signUp.email({ name, email, password });

    if (signUpError) {
      setError(signUpError.message ?? 'Could not create your account.');
      setStatus('error');
      return;
    }

    setStatus('sent');
  }

  return (
    <main className="mx-auto flex max-w-md flex-1 flex-col justify-center px-4 py-16">
      <Card>
        <h1 className="mb-1 text-2xl font-semibold text-text-primary">Create an account</h1>
        <p className="mb-6 text-sm text-text-muted">
          One account for buying and, once approved, for selling on EPX.
        </p>

        {status === 'sent' ? (
          <FormMessage tone="success">
            {"Account created. Check your email for a verification link — you'll need to verify before applying to sell."}
          </FormMessage>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            <TextField label="Name" name="name" type="text" autoComplete="name" required />
            <TextField label="Email" name="email" type="email" autoComplete="email" required />
            <TextField
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              helperText="At least 8 characters."
            />
            {error && <FormMessage tone="error">{error}</FormMessage>}
            <Button type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Creating account…' : 'Create account'}
            </Button>
          </form>
        )}

        <p className="mt-6 text-sm text-text-muted">
          Already have an account?{' '}
          <Link href="/login" className="text-text-link underline">
            Sign in
          </Link>
        </p>
      </Card>
    </main>
  );
}
